import { Backdrop, Button, CircularProgress, Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { Song } from "../../reactronica";
import DawControls from "../../components/DawComponents/DawControls/DawControls";
import { RootContainer, styles } from "./styles";
import { TrackProperties, TrackType } from "./types";
import { useDawControls } from "./useDawControls";
import { useTrackControls } from "./useTrackControls";
import InstrumentTrack from "../../components/DawComponents/InstrumentTrack/InstrumentTrack";
import AudioFileTrack from "../../components/DawComponents/AudioFileTrack/AudioFileTrack";
import { COLUMNS } from "../../components/DawComponents/InstrumentTrack/InstrumentTrack";
import UserStatusModal from "../../components/Modal/UserStatusModal";
import { AuthContext } from "../../contexts";
import { isMobile } from "react-device-detect";
import { useNavigate } from "react-router-dom";
import { APP_COLOR, APP_HOVER_COLOR, FONT_FAMILY } from "../../theme";

const Daw: React.FC<{ userVerified: boolean }> = ({ userVerified }) => {
  const defaultTrack: TrackProperties = {
    id: 1,
    name: "Track 1",
    type: TrackType.INSTRUMENT,
    volume: 5,
    pan: 0,
    solo: false,
    mute: false,
  };
  const navigate = useNavigate();
  const { userId } = useContext(AuthContext);
  const { tracks, addNewTrack, handleRemoveTrack, handleTrackFinished } =
    useTrackControls(defaultTrack);

  const {
    isPlaying,
    bpm,
    volume,
    recorder,
    exporting,
    handleVolumeChange,
    handlePlayStop,
    handleIncrementBpm,
    handleDecrementBpm,
    handleIncrementVolume,
    handleDecrementVolume,
    handleExport,
  } = useDawControls();

  const [selectedTrackId, setSelectedTrackId] = useState<number | undefined>(
    undefined
  );
  const [contextStarted, setContextStarted] = useState(false);
  const [isNameFieldFocused, setIsNameFieldFocused] = useState(false);

  const handleNameField = (dataFromChild: boolean) => {
    setIsNameFieldFocused(dataFromChild);
  };

  const handleSelectTrack = (id: number) => {
    if (id === selectedTrackId) {
      setSelectedTrackId(undefined);
    } else {
      setSelectedTrackId(id);
    }
  };
  useEffect(() => {
    function handleKeyPress(event: any) {
      if (event.keyCode === 32 && !isNameFieldFocused) {
        event.preventDefault();
        handlePlayStop();
      }
    }

    document.addEventListener("keydown", handleKeyPress);

    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [handlePlayStop, isNameFieldFocused]);

  useEffect(() => {
    if (selectedTrackId && !tracks.map((t) => t.id).includes(selectedTrackId)) {
      setSelectedTrackId(undefined);
    }
  }, [selectedTrackId, tracks]);

  useEffect(() => {
    const areInstrumentTracksFinished = tracks
      .filter((t) => t.type === TrackType.INSTRUMENT)
      .every((t) => t.trackFinished);
    const areAudioFileTracksFinished = tracks
      .filter((t) => t.type === TrackType.AUDIO_FILE)
      .every((t) => t.trackFinished);
    if (
      (isPlaying || exporting) &&
      tracks.length > 0 &&
      areInstrumentTracksFinished &&
      areAudioFileTracksFinished
    ) {
      if (isPlaying) handlePlayStop();
      if (exporting) handleExport();
    }
  }, [exporting, handleExport, handlePlayStop, isPlaying, tracks]);
  if (isMobile) {
    return (
      <div
        style={{
          backgroundColor: "black",
          height: "100vh",
          textAlign: "center",
          color: "white",
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <h4 style={{ width: "80%", marginTop: 100, marginBottom: 40 }}>
          Accessing DAW requires a desktop computer
        </h4>
        <Button
          sx={{
            paddingY: 1,
            paddingX: 2,
            fontFamily: FONT_FAMILY,
            textTransform: "none",
            color: "black",
            "&:hover": {
              backgroundColor: APP_HOVER_COLOR,
            },
            backgroundColor: APP_COLOR,
          }}
          onClick={() => navigate("/explore")}
        >
          Explore Music
        </Button>
      </div>
    );
  } else {
    return (
      <>
        <UserStatusModal
          userId={userId}
          userVerified={userVerified}
          targetPage={"/daw"}
        />
        <Backdrop
          open={!contextStarted && userId !== "" && userVerified}
          invisible={false}
          sx={{
            display: "flex",
            flexDirection: "column",
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <Typography
            sx={{ color: "white", marginTop: "10px", fontSize: "20px" }}
          >
            {`Click anywhere to start creating music...`}
          </Typography>
        </Backdrop>
        <Backdrop
          open={exporting}
          invisible={false}
          sx={{
            display: "flex",
            flexDirection: "column",
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <CircularProgress color="primary" />
          <Typography sx={{ color: "white", marginTop: "10px" }}>
            {`Exporting...`}
          </Typography>
        </Backdrop>

        <RootContainer trackCount={tracks.length}>
          <DawControls
            isPlaying={isPlaying}
            bpm={bpm}
            volume={volume}
            totalTracksCount={tracks.length}
            handlePlayStop={handlePlayStop}
            handleIncrementBpm={handleIncrementBpm}
            handleDecrementBpm={handleDecrementBpm}
            handleVolumeChange={handleVolumeChange}
            handleIncrementVolume={handleIncrementVolume}
            handleDecrementVolume={handleDecrementVolume}
            handleExport={handleExport}
          />
          <Song
            isPlaying={isPlaying}
            bpm={bpm}
            volume={volume}
            isMuted={false}
            recorder={recorder}
            exporting={exporting}
            setContextStarted={setContextStarted}
          >
            <div style={styles.measureContainer}>
              {Array.from(Array(COLUMNS / 4).keys(), (value) => (
                <div
                  key={value}
                  style={{
                    ...styles.measure,
                    width: `${100 / (COLUMNS / 4)}%`,
                  }}
                >
                  {value + 1}
                </div>
              ))}
            </div>
            <div style={styles.tracksContainer}>
              {tracks.map((track) => (
                <div key={track.id} style={styles.trackRoot}>
                  {track.type === TrackType.INSTRUMENT ? (
                    <InstrumentTrack
                      selected={track.id === selectedTrackId}
                      trackProperties={track}
                      handleSelectTrack={handleSelectTrack}
                      handleRemoveTrack={handleRemoveTrack}
                      handleTrackFinished={handleTrackFinished}
                      onNameFieldFocusChange={handleNameField}
                    />
                  ) : (
                    <AudioFileTrack
                      trackProperties={track}
                      handleRemoveTrack={handleRemoveTrack}
                      handleTrackFinished={handleTrackFinished}
                      onNameFieldFocusChange={handleNameField}
                    />
                  )}
                </div>
              ))}
            </div>
          </Song>
          <div style={styles.addTrack}>
            <Button
              variant="contained"
              sx={styles.addButton}
              onClick={() => addNewTrack(TrackType.INSTRUMENT)}
            >
              + Add track
            </Button>
            <Button
              variant="contained"
              sx={styles.addButton}
              onClick={() => addNewTrack(TrackType.AUDIO_FILE)}
            >
              + Add audio file
            </Button>
          </div>
        </RootContainer>
      </>
    );
  }
};

export default Daw;
